// src/views/Settings/account/AccountGeneralSection.jsx
//
// Los datos de la cuenta: nombre, descripción, su clave de API y la zona de
// peligro (suspender / reactivar).
//
// Vivía en un diálogo colgado de una tarjeta "Personal Account" dentro de
// `/organizations`, que es la pantalla de otra cosa: administrar tu cuenta no es
// administrar tus organizaciones, y la prueba es que la cuenta personal no
// pertenece a ninguna (`accounts.organization_id` es nulo cuando hay `owner_id`).
//
// Guardaba con `window.location.reload()` tres veces —al actualizar, al
// suspender y al reactivar—, cada una tras un `setTimeout` de 1,5 s. Recargar la
// app entera para ver un nombre nuevo tira el contexto, vuelve a pedir todo y
// hace parpadear la pantalla; ahora se refresca la cuenta y ya.
import React, { useState } from 'react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Badge } from '../../../components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../../components/ui/alert-dialog';
import { Crown, Copy, Download, Pencil } from 'lucide-react';
import AccountService from '../../../services/account.service';
import AuthService from '../../../services/auth.service';
import { PRIMARY, BUTTON_SIZE } from '../../../lib/buttonStyles';

export default function AccountGeneralSection({ account, tierInfo, onRefreshAccount }) {
  const [formData, setFormData] = useState({
    name: account?.name || '',
    description: account?.description || ''
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [showSuspendDialog, setShowSuspendDialog] = useState(false);
  const [showRefreshDialog, setShowRefreshDialog] = useState(false);
  const [newApiKey, setNewApiKey] = useState(null);
  const [showApiKeyDialog, setShowApiKeyDialog] = useState(false);

  // Edición en línea: cada campo se guarda al terminar de editarlo. Un botón
  // "Guardar cambios" para dos campos obliga a un paso que no decide nada, y
  // mientras no se pulsa deja sin saber si lo que se ve está guardado.
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({ name: '', description: '' });

  const startEditing = () => {
    setDraft({ name: formData.name || '', description: formData.description || '' });
    setEditing(true);
  };

  const saveField = async (field, raw) => {
    const value = (raw || '').trim();

    // Sin cambio no se manda nada: guardar por guardar deja un aviso de éxito
    // sobre una petición que no hacía falta.
    if (value === (formData[field] || '')) return;

    setFormData((prev) => ({ ...prev, [field]: value }));
    setLoading(true);
    setMessage(null);

    const result = await AccountService.updateAccount(account.id, { [field]: value });
    if (result.success) {
      setMessage({ type: 'success', text: 'Account updated.' });
      setTimeout(() => setMessage(null), 3000);
      await onRefreshAccount?.();
    } else {
      // Se revierte lo pintado: dejarlo con el valor nuevo diría que se guardó.
      setFormData((prev) => ({ ...prev, [field]: account?.[field] || '' }));
      setMessage({ type: 'error', text: result.error?.join(', ') || 'Update failed' });
    }
    setLoading(false);
  };

  const handleSuspend = async () => {
    setShowSuspendDialog(false);
    setLoading(true);
    const result = await AccountService.suspendAccount(account.id);
    
    if (result.success) {
      setMessage({ type: 'success', text: 'Account suspended successfully!' });
      await onRefreshAccount?.();
    } else {
      setMessage({ type: 'error', text: result.error?.join(', ') || 'Failed to suspend account' });
    }
    setLoading(false);
  };

  const handleReactivate = async () => {
    setLoading(true);
    const result = await AccountService.reactivateAccount(account.id);
    
    if (result.success) {
      setMessage({ type: 'success', text: 'Account reactivated successfully!' });
      await onRefreshAccount?.();
    } else {
      setMessage({ type: 'error', text: result.error?.join(', ') || 'Failed to reactivate account' });
    }
    setLoading(false);
  };

  const handleRefreshApiKey = async () => {
    setShowRefreshDialog(false);
    setLoading(true);
    setMessage(null);

    try {
      const result = await AuthService.refreshApiKey(account.id);
      if (result.success && result.data?.api_key) {
        setNewApiKey(result.data.api_key);
        setShowApiKeyDialog(true);
        setMessage({ type: 'success', text: 'API Key refreshed successfully!' });
        if (onRefreshAccount) {
          await onRefreshAccount();
        }
      } else {
        setMessage({ type: 'error', text: 'Failed to refresh API key' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    }
    
    setLoading(false);
  };

  const handleCopyApiKey = () => {
    navigator.clipboard.writeText(newApiKey);
    setMessage({ type: 'success', text: 'API Key copied to clipboard!' });
  };

  const handleDownloadApiKey = () => {
    const text = `Zenit AI Lab API Key\n\nAccount ID: ${account.id}\nAPI Key: ${newApiKey}\nGenerated: ${new Date().toISOString()}\n\nIMPORTANT: Keep this key secure. You won't be able to see it again.`;
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `zenit-api-key-${Date.now()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleCloseApiKeyDialog = () => {
    setShowApiKeyDialog(false);
    setNewApiKey(null);
  };

  // Mismo campo que el resto de configuración: los de aquí llevaban el borde y
  // el radio por defecto del componente y se veían de otra familia.
  const FIELD =
    'w-full rounded-xl border border-white/10 bg-black/40 px-4 py-2.5 text-sm text-white ' +
    'placeholder:text-white/35 focus:border-violet-500/60 focus:outline-none disabled:opacity-50';

  return (
    <div className="max-w-3xl space-y-8">
      {/* Una sola tarjeta para la identidad de la cuenta. Estaba repartida: los
          datos de solo lectura arriba, el nombre y la descripción abajo, y entre
          medias la clave — así que lo que describe al mismo objeto se leía en
          tres sitios. */}
      {/* La identidad de la cuenta como encabezado: el nombre y su descripción
          arriba, y debajo los datos que no se editan. Un solo lápiz para las
          dos cosas — eran dos filas con su lápiz cada una, y dos lápices en dos
          líneas seguidas piden elegir entre cosas que se editan juntas. */}
      <div>
        {editing ? (
          <div className="space-y-3">
            <Input
              autoFocus
              value={draft.name}
              placeholder="Account name"
              onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
              onBlur={() => saveField('name', draft.name)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') e.currentTarget.blur();
                if (e.key === 'Escape') setEditing(false);
              }}
              className={FIELD}
            />
            <Textarea
              rows={3}
              value={draft.description}
              placeholder="What is this account for?"
              onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
              onBlur={() => saveField('description', draft.description)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') setEditing(false);
              }}
              className={FIELD}
            />
            <button
              type="button"
              onClick={() => setEditing(false)}
              className="text-sm text-white/50 transition hover:text-white"
            >
              Done
            </button>
          </div>
        ) : (
          <div className="flex items-start gap-3">
            <div className="min-w-0">
              <h1 className="truncate text-2xl font-bold text-white">
                {formData.name || 'Untitled account'}
              </h1>
              <p className={`mt-1 text-sm ${formData.description ? 'text-white/60' : 'text-white/35'}`}>
                {formData.description || 'What is this account for?'}
              </p>
            </div>
            <button
              type="button"
              onClick={startEditing}
              aria-label="Edit account name and description"
              className="mt-1 shrink-0 rounded p-1.5 text-gray-400 transition hover:bg-white/10 hover:text-white"
            >
              <Pencil className="h-4 w-4" />
            </button>
          </div>
        )}

        {message && (
          <p
            className={`mt-3 text-sm ${
              message.type === 'success' ? 'text-emerald-400' : 'text-red-400'
            }`}
          >
            {message.text}
          </p>
        )}

        <dl className="mt-8 grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2">
          <div className="flex items-center justify-between gap-4">
            <dt className="text-sm text-gray-400">Account ID</dt>
            <dd className="truncate font-mono text-xs text-white" title={account?.id}>
              {account?.id}
            </dd>
          </div>
          <div className="flex items-center justify-between gap-4">
            <dt className="text-sm text-gray-400">Type</dt>
            <dd>
              <Badge className="border-purple-500/30 bg-purple-500/20 text-purple-400">
                {account?.type}
              </Badge>
            </dd>
          </div>
          <div className="flex items-center justify-between gap-4">
            <dt className="text-sm text-gray-400">Status</dt>
            <dd>
              <Badge
                className={
                  account?.is_active
                    ? 'border-green-500/30 bg-green-500/20 text-green-400'
                    : 'border-red-500/30 bg-red-500/20 text-red-400'
                }
              >
                {account?.is_active ? 'ACTIVE' : 'SUSPENDED'}
              </Badge>
            </dd>
          </div>
          <div className="flex items-center justify-between gap-4">
            <dt className="text-sm text-gray-400">Created</dt>
            <dd className="text-sm text-white">
              {account?.created_at ? new Date(account.created_at).toLocaleDateString() : 'N/A'}
            </dd>
          </div>
          {tierInfo && (
            <div className="flex items-center justify-between gap-4">
              <dt className="text-sm text-gray-400">Plan</dt>
              <dd className="flex items-center gap-2">
                <Crown className="h-4 w-4 text-yellow-500" />
                <span className="font-medium text-white">
                  {tierInfo.display_name || tierInfo.tier_type}
                </span>
              </dd>
            </div>
          )}
        </dl>
      </div>

      {/* La clave salía como cuatro caracteres sueltos —"0e35"— sin decir que
          eran los últimos de una clave más larga. */}
      <div className="border-t border-white/10 pt-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-medium text-white">API key</p>
            <p className="mt-1 font-mono text-sm text-white/70">
              {account?.api_key_last4 ? `••••••••••••${account.api_key_last4}` : '—'}
            </p>
          </div>
          <Button
            type="button"
            onClick={() => setShowRefreshDialog(true)}
            disabled={loading}
            variant="outline"
            className={`${BUTTON_SIZE} border-white/15 bg-white/5 text-white hover:bg-white/10`}
          >
            {loading ? 'Refreshing…' : 'Refresh'}
          </Button>
        </div>
        <p className="mt-3 text-sm text-white/45">
          Only the last four characters are shown. Refreshing generates a new key
          and stops the current one from working.
        </p>
      </div>

      {/* Suspender no es guardar: separado de la tarjeta que se edita, para que
          no se pulse por estar al lado. */}
      <div className="border-t border-white/10 pt-6">
        <p className="text-sm font-medium text-white">
          {account?.is_active ? 'Suspend account' : 'Reactivate account'}
        </p>
        <p className="mb-4 mt-1 text-sm text-white/50">
          {account?.is_active
            ? 'Its agents, workflows and API key stop working until it is reactivated.'
            : 'Everything in this account starts working again.'}
        </p>
        {account?.is_active ? (
          <Button
            type="button"
            onClick={() => setShowSuspendDialog(true)}
            disabled={loading}
            variant="outline"
            className={`${BUTTON_SIZE} border-red-500/30 bg-red-500/20 text-red-400 hover:bg-red-500/30`}
          >
            Suspend account
          </Button>
        ) : (
          <Button
            type="button"
            onClick={handleReactivate}
            disabled={loading}
            variant="outline"
            className={`${BUTTON_SIZE} border-green-500/30 bg-green-500/20 text-green-400 hover:bg-green-500/30`}
          >
            Reactivate account
          </Button>
        )}
      </div>

      {/* Suspend Account Alert Dialog */}
      <AlertDialog open={showSuspendDialog} onOpenChange={setShowSuspendDialog}>
        <AlertDialogContent className="bg-[#0a0a0a] border-white/20 text-white backdrop-blur-xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Suspend Account</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              Are you sure you want to suspend this account?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel asChild>
              <Button variant="outline" className="bg-gray-700 hover:bg-gray-800 text-white border-white/20">Cancel</Button>
            </AlertDialogCancel>
            <AlertDialogAction asChild>
              <Button onClick={handleSuspend} className="bg-red-600 hover:bg-red-700 text-white">Suspend</Button>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Refresh API Key Alert Dialog */}
      <AlertDialog open={showRefreshDialog} onOpenChange={setShowRefreshDialog}>
        <AlertDialogContent className="bg-[#0a0a0a] border-white/20 text-white backdrop-blur-xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Refresh API Key</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              Are you sure you want to refresh your API key? The old key will stop working.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel asChild>
              <Button variant="outline" className="bg-gray-700 hover:bg-gray-800 text-white border-white/20">Cancel</Button>
            </AlertDialogCancel>
            <AlertDialogAction asChild>
              <Button onClick={handleRefreshApiKey} className="bg-blue-600 hover:bg-blue-700 text-white">Refresh</Button>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Show New API Key Dialog */}
      <AlertDialog open={showApiKeyDialog} onOpenChange={handleCloseApiKeyDialog}>
        <AlertDialogContent className="bg-[#0a0a0a] border-white/20 text-white backdrop-blur-xl max-w-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-xl">Your New API Key</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              This is your only chance to see the complete API key. Make sure to save it securely.
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-black/40 border border-white/10 rounded-lg p-4">
              <code className="text-sm text-green-400 break-all font-mono">{newApiKey}</code>
            </div>
            
            <div className="flex gap-3">
              <Button
                onClick={handleCopyApiKey}
                className={`${BUTTON_SIZE} flex-1 bg-blue-600 text-white hover:bg-blue-700`}
              >
                <Copy className="w-4 h-4 mr-2" />
                Copy to Clipboard
              </Button>
              <Button
                onClick={handleDownloadApiKey}
                className={PRIMARY}
              >
                <Download className="w-4 h-4 mr-2" />
                Download as File
              </Button>
            </div>
            
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
              <p className="text-yellow-400 text-sm font-medium">
                ⚠️ Warning: Once you close this window, you won't be able to see the complete key again.
              </p>
            </div>
          </div>
          
          <AlertDialogFooter>
            <AlertDialogAction asChild>
              <Button onClick={handleCloseApiKeyDialog} className={`${BUTTON_SIZE} w-full bg-gray-700 text-white hover:bg-gray-800`}>
                I've Saved My Key
              </Button>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
